import { HDR_CREDIT_COPY, HDR_FREE_ALLOWANCE } from '@rehub/pricing';

export const LANDING_H1 = 'AI HDR Photo Editing for Property Photographers';

export const LANDING_SUPPORTING =
  'Turn bracketed property photos into bright, balanced images with natural colours and clear window views. Upload your shoot, review the results and download your finished images.';

export const LANDING_TITLE = 'AI HDR Photo Editing for Property Photographers | HDR Photo';

export const LANDING_DESCRIPTION =
  'Merge bracketed property photos with AI HDR editing. Create balanced interiors, natural colours and clear window views. Explore HDR Photo by ReHub Studio.';

export const LANDING_CANONICAL = 'https://hdrphoto.rehub.studio/';

export const CTA_TRY_FREE = 'Try 10 images free';
export const CTA_SEE_RESULTS = 'See the results';

export const FREE_OFFER_LINE = `${HDR_FREE_ALLOWANCE} HDR Credits every month. No credit card required.`;

export const HERO_COMPARE = {
  beforeSrc: '/hero/hdr-bracket-mid.webp',
  afterSrc: '/hero/hdr-result.webp',
  beforeAlt:
    'Middle exposure of a property interior, holding the room but leaving the window view bright',
  afterAlt:
    'The same interior after the bracketed exposures have been merged into a balanced HDR photograph',
  beforeLabel: 'Middle exposure',
  afterLabel: 'Merged HDR',
  width: 1536,
  height: 1024,
  caption:
    'Matching demonstration plates: the mid frame compared with the merged HDR. The darker and brighter exposures used for this result are shown underneath.',
};

export const HERO_BRACKETS = [
  {
    src: '/hero/hdr-bracket-dark.webp',
    alt: 'Dark exposure preserving the exterior view through the windows',
    label: 'Dark · windows',
  },
  {
    src: '/hero/hdr-bracket-mid.webp',
    alt: 'Middle exposure capturing the main interior lighting',
    label: 'Mid · interior',
  },
  {
    src: '/hero/hdr-bracket-bright.webp',
    alt: 'Bright exposure revealing detail in the room shadows',
    label: 'Bright · shadows',
  },
] as const;

export const HERO_POINTS = [
  'Automatic bracket grouping',
  'Batch a whole shoot',
  'High-resolution JPEG output',
] as const;

export const GALLERY_EXAMPLES = [
  {
    id: 'bright-window',
    title: 'Bright window, darker room',
    caption:
      'A single mid exposure cannot hold both a sunlit view and a naturally lit interior. The merge is designed to keep highlight detail from the darker frames.',
    beforeSrc: '/gallery/us-great-room-hdr-before.webp',
    afterSrc: '/gallery/us-great-room-hdr-after.webp',
    beforeAlt:
      'Single exposure of a living room, holding the garden view but leaving the interior in shadow',
    afterAlt:
      'The same living room after exposures have been merged into a balanced HDR photograph',
    beforeLabel: 'Middle exposure',
    afterLabel: 'Merged HDR',
  },
  {
    id: 'mixed-lighting',
    title: 'Mixed daylight and interior light',
    caption:
      'Kitchens often mix warm fittings with cool window light. White-balance correction after the merge is aimed at neutral walls and believable worktops.',
    beforeSrc: '/gallery/us-kitchen-hdr-before.webp',
    afterSrc: '/gallery/us-kitchen-hdr-after.webp',
    beforeAlt:
      'Single exposure of a kitchen, holding the window view but leaving the island and cabinets in shadow',
    afterAlt:
      'The same kitchen after exposures have been merged into a balanced HDR photograph',
    beforeLabel: 'Middle exposure',
    afterLabel: 'Merged HDR',
  },
] as const;

export const PROPERTY_SET = [
  {
    src: '/gallery/us-great-room-hdr-after.webp',
    alt: 'Merged HDR of a living room with a visible garden view',
    label: 'Living room',
  },
  {
    src: '/gallery/us-kitchen-hdr-after.webp',
    alt: 'Merged HDR of a kitchen with balanced interior and window light',
    label: 'Kitchen',
  },
  {
    src: '/gallery/us-bedroom-hdr-after.webp',
    alt: 'Balanced bedroom photograph with daylight at the window',
    label: 'Bedroom',
  },
  {
    src: '/gallery/us-exterior-hdr-after.webp',
    alt: 'Balanced exterior photograph of a house façade',
    label: 'Exterior',
  },
] as const;

export const WORKFLOW = [
  {
    title: 'Upload your shoot',
    description:
      'Drag in JPEGs, PNGs or TIFFs — RAW files (CR2, NEF, ARW, DNG) are accepted by the uploader. Capture times are read from EXIF so frames shot within about two seconds become one HDR stack, and files from the same folder become one listing.',
  },
  {
    title: 'Process your images',
    description:
      'Stacks of two or more frames are aligned and merged. Colour, white balance, window detail and vertical correction are applied on the same path. Single photographs that are not part of a bracket are still enhanced. Jobs run one after another in the queue.',
  },
  {
    title: 'Review and download',
    description:
      'Open the listing in the studio and compare each finished image with the first source file. Download individual high-resolution JPEGs or the whole set as a ZIP.',
  },
] as const;

export const BENEFITS = [
  {
    title: 'Bracket merging that holds the view',
    body: 'Highlight detail is taken from the darker frames so bright windows do not have to blow out to white. The merge recovers captured detail — it cannot invent a view that was never exposed.',
  },
  {
    title: 'Natural colour and white balance',
    body: 'Mixed interior lighting is corrected toward neutral walls and believable wood tones, rather than an oversaturated HDR treatment.',
  },
  {
    title: 'Alignment of small movement',
    body: 'Frames are aligned before they are merged, which helps with slight handheld movement, curtains and foliage. A large sidestep or zoom change between shots cannot be repaired.',
  },
  {
    title: 'Vertical and perspective correction',
    body: 'Converging verticals are straightened as a separate step from aligning the bracket, so walls sit more naturally in listing photographs.',
  },
  {
    title: 'Batch a whole property',
    body: 'Upload the folder, let stacks process in the background, then download the finished set. Each completed image uses one HDR Credit.',
  },
  {
    title: 'High-resolution JPEG output',
    body: 'Finished images download as high-resolution JPEGs, suitable for property portals, brochures and most agency websites.',
  },
] as const;

export const WORKFLOW_EXAMPLE = {
  title: 'A typical listing shoot',
  body: 'Photograph each room as a short bracket — three frames at about −2 / 0 / +2 EV is the usual starting point, with five frames for hard sun in the garden. Keep one property in one folder. HDR Photo groups the burst, merges it, and returns a JPEG you can check against the first source file before you deliver the set.',
};

export const SISTER_APPS = [
  {
    name: 'PhotoClear',
    description: 'Remove clutter from a finished photograph.',
    url: 'https://photoclear.rehub.studio',
    logo: '/photoclear-logo-transparent.png',
  },
  {
    name: 'Virtual Staging',
    description: 'Furnish an empty room for listing marketing.',
    url: 'https://staging.rehub.studio',
    logo: '/virtual-staging-logo-transparent.png',
  },
  {
    name: 'HyperDusk',
    description: 'Turn a daytime exterior into dusk.',
    url: 'https://hyperdusk.rehub.studio',
    logo: '/hyperdusk-logo-wordmark-alpha.png',
  },
  {
    name: 'ImageMotion',
    description: 'Turn a still into cinematic property video.',
    url: 'https://imagemotion.rehub.studio',
    logo: '/imagemotion-logo.webp',
  },
] as const;

export const HOME_FAQS: { q: string; a: string }[] = [
  {
    q: 'Which file formats can I upload?',
    a: 'JPEG and PNG are supported. The uploader also accepts RAW files (CR2, NEF, ARW, DNG). TIFF files are not accepted. JPEG is the recommended format for a complete property shoot.',
  },
  {
    q: 'How many exposures should I take?',
    a: 'Three frames are the usual starting point for interiors, often at about −2 / 0 / +2 EV. Five frames help when the garden is in hard sun and the room is deep. HDR Photo also groups two-frame stacks and can enhance a single photograph when a full bracket was not captured.',
  },
  {
    q: 'Can I enhance a single photograph?',
    a: 'Yes. Images that are not part of a timed bracket are treated as singles and still receive colour, white balance and vertical correction. A full bracket usually holds more window and shadow detail because that information was captured in the darker and brighter frames.',
  },
  {
    q: 'How does automatic bracket grouping work?',
    a: 'Capture time is read from EXIF. Frames shot within about two seconds become one HDR stack, and images from the same folder become one listing. Do not interleave two rooms in the same two-second burst.',
  },
  {
    q: 'What happens with handheld movement or moving objects?',
    a: 'Frames are auto-aligned, so minor handheld movement is corrected. Leaves, curtains and people can still mismatch between exposures — the merge will prefer one frame in that area. A tripod remains the most reliable method for bright windows.',
  },
  {
    q: 'What resolution and formats can I download?',
    a: 'Finished images download as high-resolution JPEGs. You can download one image or the whole listing as a ZIP.',
  },
  {
    q: 'How long does processing typically take?',
    a: 'Processing is automatic and runs one image at a time in the queue. Time depends on image size and how many photographs are already waiting. A full shoot is processed sequentially rather than all at once.',
  },
  {
    q: 'What does an HDR Credit cover?',
    a: HDR_CREDIT_COPY.optimisedHelper,
  },
  {
    q: 'What is included in the free offer?',
    a: `Every account includes ${HDR_FREE_ALLOWANCE} HDR Credits each month — enough for ${HDR_FREE_ALLOWANCE} finished edits. Failed jobs are not charged. A new completed merge uses another HDR Credit. Unused monthly credits do not accumulate as a larger balance.`,
  },
  {
    q: 'Can I use my existing ReHub account?',
    a: 'Yes. HDR Photo uses the same login as PhotoClear, Virtual Staging and the rest of ReHub. HDR processing uses HDR Credits. ReHub Credits stay available for the other apps and are not deducted for an HDR merge.',
  },
];
